import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, User, Globe, LogOut, MessageSquarePlus, Mic, Volume2, HelpCircle, Code } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const MobileHamburgerMenu = ({
  theme,
  isAdmin,
  user,
  onLogout,
  onNewChat,
  onLanguageClick,
  enableVoiceToVoice,
  onToggleVoiceToVoice,
  onStartProductTour,
  onClose
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(true); // Start open when rendered

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (onClose) onClose();
  };

  const closeMenu = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleMenuAction = (action) => {
    if (typeof action === 'function') {
      action();
    }
    closeMenu();
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      <div 
        className="mobile-menu-overlay"
        onClick={closeMenu}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Mobile Menu — stopPropagation so overlay doesn't steal taps */}
      <div 
        className={`mobile-menu mobile-menu-open`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '280px',
          height: '100vh',
          maxHeight: '100dvh',
          background: theme.modalBg,
          borderLeft: `1px solid ${theme.primaryBorder}`,
          zIndex: 1001,
          transform: 'translateX(0)',
          transition: 'transform 0.3s ease',
          overflowY: 'auto',
          padding: '20px 0',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'manipulation'
        }}
      >
        {/* Menu Header */}
        <div style={{
          padding: '0 20px 20px 20px',
          borderBottom: `1px solid ${theme.primaryBorder}`,
          marginBottom: '20px'
        }}>
          <div
            onClick={() => {
              if (onNewChat) onNewChat();
              navigate('/fullchat');
              if (onClose) onClose();
              else setIsOpen(false);
            }}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (onNewChat) onNewChat();
                navigate('/fullchat');
                if (onClose) onClose();
                else setIsOpen(false);
              }
            }}
            title="Go to main page"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              cursor: 'pointer'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '700',
              fontSize: '18px'
            }}>
              O
            </div>
            <div>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: theme.primaryText
              }}>
                Oakland AI
              </div>
              <div style={{
                fontSize: '12px',
                color: theme.secondaryText
              }}>
                Mobile Menu
              </div>
            </div>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div style={{
            padding: '0 20px 20px 20px',
            borderBottom: `1px solid ${theme.primaryBorder}`,
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.primaryText
                }}>
                  {user.name || 'User'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.secondaryText
                }}>
                  {isAdmin ? 'Admin' : 'User'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Menu Items */}
        <div style={{ padding: '0 20px' }}>
          {/* View Profile — single source for profile from menu */}
          <button
            type="button"
            onClick={() => {
              closeMenu();
              navigate('/profile');
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              color: theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <User size={18} />
            {t('common.viewProfile')}
          </button>

          {/* Voice Toggle */}
          <button
            type="button"
            onClick={() => handleMenuAction(onToggleVoiceToVoice)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: enableVoiceToVoice ? theme.accentButton : theme.cardBg,
              border: `1px solid ${enableVoiceToVoice ? theme.accentButton : theme.primaryBorder}`,
              borderRadius: '12px',
              color: enableVoiceToVoice ? 'white' : theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {enableVoiceToVoice ? <Volume2 size={18} /> : <Mic size={18} />}
            {enableVoiceToVoice ? t('fullchat.voiceOn') : t('fullchat.voiceOff')}
          </button>

          {/* New Chat */}
          <button
            type="button"
            onClick={() => handleMenuAction(onNewChat)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              color: theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <MessageSquarePlus size={18} />
            {t('fullchat.newChat')}
          </button>


          {/* Language — Globe icon to match header */}
          <button
            type="button"
            onClick={() => {
              if (typeof onLanguageClick === 'function') {
                onLanguageClick();
              }
              closeMenu();
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              color: theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Globe size={18} />
            {t('common.language')}
          </button>

          {/* Developer */}
          <button
            type="button"
            onClick={() => {
              // Check #4: CRITICAL - Verify DeveloperPage route exists before navigation
              try {
                navigate('/developer');
                closeMenu();
              } catch (error) {
                // CRITICAL ERROR: DeveloperPage route is missing
                console.error('❌ CRITICAL ERROR: Cannot navigate to /developer - DeveloperPage is required!', error);
                closeMenu();
                alert('CRITICAL ERROR: DeveloperPage is required but missing. The application cannot function without it. Please restore frontend/src/pages/DeveloperPage.jsx');
                throw new Error('DeveloperPage route is required but missing');
              }
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              color: theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Code size={18} />
            {t('navigation.developer')}
          </button>

          {/* Help */}
          <button
            type="button"
            onClick={() => handleMenuAction(onStartProductTour)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: '12px',
              color: theme.primaryText,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <HelpCircle size={18} />
            {t('fullchat.helpAndTour')}
          </button>

          {/* Logout */}
          <button
            type="button"
            onClick={() => handleMenuAction(onLogout)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              color: '#ef4444',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <LogOut size={18} />
            {t('common.logout')}
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileHamburgerMenu;
